/* eslint-disable react/jsx-no-bind -- pre-existing legacy handlers; this change only removes the placeholder border override */
import ChevronRight from '@mui/icons-material/ChevronRight'
import ExpandMore from '@mui/icons-material/ExpandMore'
import Box from '@mui/material/Box'
import Collapse from '@mui/material/Collapse'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemText from '@mui/material/ListItemText'
import Tooltip from '@mui/material/Tooltip'
import { groupBy } from 'lodash'
import orderBy from 'lodash/orderBy'
import PropTypes from 'prop-types'
import React, { Children, useState } from 'react'
import { shallowEqual, useSelector } from 'react-redux'

import HHSectionPlaceholder from 'components/common/HHSectionPlaceholder'
import T from 'T'
import { get } from 'utils/lodash'

import ContactStatusAvatar from '../../../common/avatar/ContactStatusAvatar'

import AddEditContactDialog from './AddEditContactDialog'
import ContactsActionMenu from './ContactsActionMenu'
import ContactView from './ContactView'
import { getContactState } from './utils'

const ContactsListing = ({ contacts = [], showActionMenu = false, noContactMessage = T.NO_CONTACTS, bulkExpansion }) => {
  const customerDetails = useSelector(state => get(state, 'CustomersReducer.customerDetails'), shallowEqual)
  const [isOpenAddEditDialog, setIsOpenAddEditDialog] = useState(false)
  const [contactsAnchorEl, setContactsAnchorEl] = useState(null)
  const [selectedContact, setSelectedContact] = useState(null)
  const [isOpenDeactivatedList, setIsOpenDeactivatedList] = useState(false)

  // Sort contact
  const allContacts = orderBy(contacts, ['deactivated', 'firstName', 'lastName'])
  const groupedContacts = groupBy(allContacts, ({ deactivated }) => deactivated)
  const activeContacts = get(groupedContacts, 'false', [])
  const deactivatedContacts = get(groupedContacts, 'true', [])
  const accountId = get(customerDetails, 'id')

  const handleOpenActionMenu = event => {
    event.stopPropagation()
    setContactsAnchorEl(event.currentTarget)
  }

  const handleCloseActionMenu = () => {
    setContactsAnchorEl(null)
    setSelectedContact(null)
  }

  const openEditDialogForContact = contact => {
    setSelectedContact(getContactState(customerDetails, contact))
    setIsOpenAddEditDialog(true)
  }

  const toggleDeactivatedListOpen = () => {
    setIsOpenDeactivatedList(s => !s)
  }

  return (
    <>
      {activeContacts.length === 0 && deactivatedContacts.length === 0 && <HHSectionPlaceholder title={noContactMessage} />}
      {activeContacts.length > 0 && (
        <List disablePadding>
          {Children.toArray(
            activeContacts.map((contact, index) => (
              <>
                <ContactView
                  bulkExpansion={bulkExpansion}
                  contact={contact}
                  showActionMenu={showActionMenu}
                  onActionClick={event => {
                    setSelectedContact(getContactState(customerDetails, contact))
                    handleOpenActionMenu(event)
                  }}
                  onRowClick={showActionMenu ? () => openEditDialogForContact(contact) : undefined}
                />
                {(activeContacts.length - 1 !== index || (activeContacts.length - 1 === index && deactivatedContacts.length !== 0)) && (
                  <Divider />
                )}
              </>
            ))
          )}
        </List>
      )}
      {deactivatedContacts.length !== 0 && (
        <>
          <ListItem
            divider
            secondaryAction={
              <Box mr={0.5}>
                <Tooltip arrow title={T.DEACTIVATED_ACCOUNT}>
                  <span>
                    <ContactStatusAvatar isActive={false} />
                  </span>
                </Tooltip>
              </Box>
            }
          >
            <IconButton size="small" onClick={toggleDeactivatedListOpen}>
              {isOpenDeactivatedList ? <ExpandMore /> : <ChevronRight />}
            </IconButton>
            <ListItemText
              primary={T.DISABLED_CONTACTS}
              slotProps={{
                primary: { color: 'textSecondary' },
              }}
            />
          </ListItem>
          <Collapse in={isOpenDeactivatedList}>
            <List disablePadding>
              {Children.toArray(
                deactivatedContacts.map((contact, index) => (
                  <>
                    <ContactView
                      bulkExpansion={bulkExpansion}
                      contact={contact}
                      showActionMenu={showActionMenu}
                      onActionClick={event => {
                        setSelectedContact(getContactState(customerDetails, contact))
                        handleOpenActionMenu(event)
                      }}
                      onRowClick={showActionMenu ? () => openEditDialogForContact(contact) : undefined}
                    />
                    {deactivatedContacts.length - 1 !== index && <Divider />}
                  </>
                ))
              )}
            </List>
          </Collapse>
        </>
      )}
      <ContactsActionMenu
        accountId={accountId}
        anchorEl={contactsAnchorEl}
        selectedContact={selectedContact}
        onClose={handleCloseActionMenu}
        onEdit={() => {
          setContactsAnchorEl(null)
          setIsOpenAddEditDialog(true)
        }}
      />
      {isOpenAddEditDialog && (
        <AddEditContactDialog
          accountId={accountId}
          contact={selectedContact}
          isOpen={isOpenAddEditDialog}
          onClose={() => {
            setSelectedContact(null)
            setIsOpenAddEditDialog(false)
          }}
        />
      )}
    </>
  )
}

ContactsListing.propTypes = {
  contacts: PropTypes.array,
  showActionMenu: PropTypes.bool,
  noContactMessage: PropTypes.string,
  bulkExpansion: PropTypes.shape({
    open: PropTypes.bool,
    version: PropTypes.number,
  }),
}

export default ContactsListing
